import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import type { EntryPointProps } from 'react-relay';
import type { ActionMeta, OptionProps, StylesConfig } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type {
	OptionResponse,
	SelectOption,
	SelectValue,
	SelectComponentsConfig,
	SelectedValue,
} from '../../common/types.tsx';
import { isSingleValueOption } from '../../common/utils/is-single-value-option.tsx';
import ClearSelectedItemsButton from './clear-selection-button/index.tsx';
import { MenuList } from './menu-list/main.tsx';
import { OperatorsSelect } from './operator-select/index.tsx';
import PaginationDetails from './pagination-details/index.tsx';
import RestoreDefaultsButton from './restore-defaults-button/index.tsx';
import { singleSelectComponents, BaseSelect, selectComponents } from './select/main.tsx';
import Option from './select/option/index.tsx';
import ShowFullListButton from './show-full-list-button/index.tsx';
import type { IsError, Props } from './types.tsx';
import { generateOptions, getValueFromOptions } from './utils.tsx';

const MAX_TOTAL_COUNT = 1000; // To be cleaned up in EM-3568

const addFullListToOptions = (options: SelectValue, fullList: boolean): SelectValue =>
	options.map((option: SelectOption): SelectOption => {
		switch (option.optionType) {
			case 'group': {
				return {
					...option,
					options: addFullListToOptions(option.options, fullList),
				};
			}
			// Need separate cases so flow can correctly infer types for this union
			case 'avatar': {
				return { ...option, fullList };
			}
			// Need separate cases so flow can correctly infer types for this union
			case 'lozenge': {
				return { ...option, fullList };
			}
			// Need separate cases so flow can correctly infer types for this union
			case 'option': {
				return { ...option, fullList };
			}
			default: {
				return option;
			}
		}
	});

const selectStyles: StylesConfig<SelectOption, boolean> = {
	container: (baseStyles) => ({
		...baseStyles,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	}),
};

const PaginatedAsyncSelectView = ({
	id,
	onMenuScrollToBottom,
	onMenuScrollToTop,
	placeholder,
	invalidLabel,
	enableFullList: enableFullListProp,
	operandDisplayValues,
	showOperandDisplayValuesInFullList,
	loadOptions,
	onInputChange,
	value,
	onChange,
	onChangeOperator,
	onClear,
	onRestoreDefaults,
	components: customComponents,
	showOperatorSelect,
	allowedOperators,
	selectedOperator,
	isMulti = true,
	setInitialFocusRef,
	isLoading: isLoadingExternal,
	CustomFooter,
	isInfiniteScrollEnabled,
	...restProps
}: Props) => {
	const { formatMessage } = useIntl();
	const paginatedComponents = useMemo(
		() => ({
			...selectComponents,
			MenuList,
		}),
		[],
	);
	const paginatedSingleSelectComponents = useMemo(
		() => ({
			...singleSelectComponents,
			Option: (props: OptionProps<SelectOption, true>) => <Option {...props} isMulti={false} />,
			MenuList,
		}),
		[],
	);
	const [isLoadingInternal, setIsLoading] = useState<boolean>(true);
	const [isPaginationLoading, setIsPaginationLoading] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string>('');
	const [loadedOptions, setLoadedOptions] = useState<SelectValue>([]);
	const [enableFullList, setEnableFullList] = useState<boolean>(enableFullListProp || false);
	const [totalCount, setTotalCount] = useState<number | null>(null);
	const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
	const [error, setError] = useState<IsError>({
		isError: false,
		error: null,
		onRetry: null,
	});

	const isLoading = isLoadingInternal || isLoadingExternal;

	const searchRef = useRef('');

	const itemCount = useMemo(
		() =>
			loadedOptions.flatMap((option) => (option.optionType === 'group' ? option.options : option))
				.length,
		[loadedOptions],
	);

	const getTotalCountWithFunctions = useCallback<(arg1: number | null) => number | null>(
		(newTotalCount) => {
			if (newTotalCount === null || !operandDisplayValues?.length) {
				return newTotalCount;
			}

			let newTotalCountWithKeywords: number;

			if (showOperandDisplayValuesInFullList === true) {
				newTotalCountWithKeywords = newTotalCount + operandDisplayValues.length;
			} else {
				newTotalCountWithKeywords =
					newTotalCount +
					value.filter(
						(option) => isSingleValueOption(option) && operandDisplayValues.includes(option.value),
					).length;
			}

			return newTotalCountWithKeywords < MAX_TOTAL_COUNT
				? newTotalCountWithKeywords
				: MAX_TOTAL_COUNT;
		},
		[operandDisplayValues, showOperandDisplayValuesInFullList, value],
	);

	const checkIfError = useCallback(
		(search: string, options: SelectValue, onRetry: () => void): boolean => {
			const errorOption = options.find(
				(option) => option.optionType === 'error' && option.error instanceof Error,
			);

			if (errorOption && errorOption.optionType === 'error') {
				setError({
					isError: true,
					error: errorOption.error,
					onRetry,
				});
				setIsLoading(false);
				return true;
			}

			setError({
				isError: false,
				error: null,
				onRetry: null,
			});
			return false;
		},
		[],
	);

	const handleLoadOptions = useCallback(
		(
			search: string,
			overridenValue?: SelectValue,
			renderInitialValues: boolean = enableFullList,
		): void => {
			if (!loadOptions) {
				return;
			}
			searchRef.current = search;
			setIsLoading(true);
			loadOptions(
				search,
				renderInitialValues,
				undefined,
				({ options, totalCount: newTotalCount, endCursor: newEndCursor }: OptionResponse) => {
					// Handle error
					if (
						checkIfError(search, options, () => {
							handleLoadOptions(search);
						})
					)
						return;

					// This is to prevent outdated options/results from overriding if a race condition occurs
					if (searchRef.current !== search) return;

					if (search) {
						if (!isEqual(loadedOptions, options)) {
							setLoadedOptions(options);
							setTotalCount(newTotalCount);
						}
					} else {
						const newLoadedOptions = generateOptions(
							[...(overridenValue ?? value)],
							options,
							formatMessage,
						);

						if (!isEqual(loadedOptions, newLoadedOptions)) {
							setLoadedOptions(newLoadedOptions);
							setTotalCount(getTotalCountWithFunctions(newTotalCount));
						}
					}

					setEndCursor(newEndCursor);
					setIsLoading(false);
				},
			);
		},
		[
			loadOptions,
			enableFullList,
			checkIfError,
			value,
			loadedOptions,
			getTotalCountWithFunctions,
			formatMessage,
		],
	);

	const handleShowMore = useCallback(
		(firstLoad = false, isRetry = false) => {
			if (!loadOptions || !loadedOptions) {
				return;
			}

			let originalLoadedOptions: SelectValue;
			if (firstLoad) {
				originalLoadedOptions = [];
				setIsLoading(true);
			} else {
				originalLoadedOptions = loadedOptions;
				if (isRetry) {
					setIsLoading(true);
				} else {
					setIsPaginationLoading(true);
				}
			}

			loadOptions(
				inputValue,
				false,
				endCursor,
				({ options, totalCount: newTotalCount, endCursor: newEndCursor }) => {
					if (
						checkIfError(inputValue, options, () => {
							handleShowMore(firstLoad, true);
						})
					)
						return;

					const newLoadedOptions = inputValue
						? originalLoadedOptions.concat(options)
						: generateOptions(value, options, formatMessage, originalLoadedOptions, firstLoad);

					setEnableFullList(false);
					setTotalCount(inputValue ? newTotalCount : getTotalCountWithFunctions(newTotalCount));
					setLoadedOptions(newLoadedOptions);
					setIsPaginationLoading(false);
					setIsLoading(false);
					setEndCursor(newEndCursor);
				},
			);
		},
		[
			loadOptions,
			loadedOptions,
			inputValue,
			endCursor,
			checkIfError,
			value,
			getTotalCountWithFunctions,
			formatMessage,
		],
	);

	const handleShowFullList = useCallback(() => {
		handleShowMore(true);
	}, [handleShowMore]);

	const handleInputChange = useCallback(
		(newInputValue: string) => {
			// on empty search: reset to default state for pickers where enableFullListProp==true
			if (newInputValue.length === 0 && enableFullListProp) {
				setEnableFullList(true);
				handleLoadOptions(newInputValue, undefined, true);
			} else {
				setEnableFullList(false);
				handleLoadOptions(newInputValue);
			}
			setInputValue(newInputValue);
			onInputChange && onInputChange();
		},
		[enableFullListProp, handleLoadOptions, onInputChange],
	);

	const handleOnChange = useCallback(
		(options: SelectedValue, meta: ActionMeta<SelectOption>): void => {
			if ('option' in meta && meta.option) {
				const selectedIndex = findIndex(
					loadedOptions,
					(option) =>
						isSingleValueOption(option) &&
						meta.option !== undefined &&
						isSingleValueOption(meta.option) &&
						option.value === meta.option.value,
				);

				onChange &&
					onChange(
						options,
						meta,
						{
							selectedIndex,
						},
						undefined,
						fg('jira_inline_field_config_gate') ? () => setLoadedOptions(loadedOptions) : undefined,
					);
			} else {
				onChange &&
					onChange(
						options,
						meta,
						undefined,
						undefined,
						fg('jira_inline_field_config_gate') ? () => setLoadedOptions(loadedOptions) : undefined,
					);
			}
			if (fg('jira_inline_field_config_gate')) {
				const newLoadedOptions = generateOptions(
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					options as SelectOption[],
					loadedOptions,
					formatMessage,
				);
				if (!isEqual(loadedOptions, newLoadedOptions)) {
					setLoadedOptions(newLoadedOptions);
				}
			}
		},
		[onChange, loadedOptions, formatMessage],
	);

	const handleOnRestoreDefaults = useCallback(() => {
		if (typeof onRestoreDefaults === 'function') {
			const defaultOptions = onRestoreDefaults();
			// Reset picker input and force update of options to go back to an initial-like state where selected options are on top
			setInputValue('');
			handleLoadOptions('', defaultOptions);
		}
	}, [handleLoadOptions, onRestoreDefaults]);

	const handleMenuScrollToBottom = useCallback(() => {
		onMenuScrollToBottom?.();
		if (fg('jira_inline_field_config_gate')) {
			if (isInfiniteScrollEnabled && totalCount !== null && itemCount < totalCount) {
				handleShowMore();
			}
		}
	}, [isInfiniteScrollEnabled, totalCount, itemCount, handleShowMore, onMenuScrollToBottom]);

	// We only want to call this on init, so it's ok to disable react-hooks/exhaustive-deps
	useEffect(() => {
		handleLoadOptions('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadedOptionsWithFullList = useMemo(
		() => addFullListToOptions(loadedOptions, !enableFullList),
		[loadedOptions, enableFullList],
	);

	const components = useMemo<SelectComponentsConfig>(
		() =>
			isMulti
				? {
						...paginatedComponents,
						...customComponents,
					}
				: { ...paginatedSingleSelectComponents, ...customComponents },
		[customComponents, isMulti, paginatedComponents, paginatedSingleSelectComponents],
	);

	const selectedValue: SelectValue = useMemo(
		/**
		 * This method is to form the value from loadedOptionsWithFullList Array so that the 'Options' and 'Value' props of
		 * select component will have same object references, This is a workaround to meet the below condition in the '@atlaskit/select'
		 * component's ariaFocused method
		 * var isSelected = !!(focusedOption && selectValue && selectValue.includes(focusedOption));.
		 * This condition must work to announce the state of selection.
		 *
		 * The below method can be removed if the above condition is improved to check just the value instead of reference.
		 */
		() => getValueFromOptions(loadedOptionsWithFullList, value),
		[loadedOptionsWithFullList, value],
	);

	const showFooterType = useMemo(() => {
		const showFooter =
			!isLoading && loadedOptions && Boolean(loadedOptions.length) && !error.isError;

		if (showFooter && CustomFooter != null && fg('jira_inline_field_config_gate')) {
			return 'custom';
		}

		if (showFooter) {
			return 'default';
		}
	}, [CustomFooter, error.isError, isLoading, loadedOptions]);

	return (
		<>
			{showOperatorSelect &&
				onChangeOperator &&
				allowedOperators &&
				Boolean(allowedOperators.length) && (
					<OperatorsSelect
						onChange={onChangeOperator}
						allowedOperators={allowedOperators}
						selectedOperator={selectedOperator}
					/>
				)}
			<BaseSelect
				components={components}
				inputValue={inputValue}
				onInputChange={handleInputChange}
				onMenuScrollToBottom={handleMenuScrollToBottom}
				onMenuScrollToTop={onMenuScrollToTop}
				placeholder={placeholder}
				id={id}
				invalidLabel={invalidLabel}
				options={loadedOptionsWithFullList}
				setInitialFocusRef={setInitialFocusRef}
				menuListProps={{
					error,
					handleShowMore,
					isLoading,
					isPaginationLoading,
					showShowMoreButton: fg('jira_inline_field_config_gate')
						? totalCount !== null && itemCount < totalCount && !isInfiniteScrollEnabled
						: totalCount !== null && itemCount < totalCount,
				}}
				onChange={handleOnChange}
				value={selectedValue}
				styles={selectStyles}
				{...restProps}
				isMulti={isMulti}
				totalCount={totalCount}
			/>
			{showFooterType === 'default' && (
				<Footer>
					{onRestoreDefaults !== undefined
						? typeof onRestoreDefaults === 'function' && (
								<RestoreDefaultsButton onRestoreDefaults={handleOnRestoreDefaults} />
							)
						: Boolean(value.length) && isMulti && <ClearSelectedItemsButton onClear={onClear} />}
					{(!value.length || !isMulti) && enableFullList && (
						<ShowFullListButton onShowFullList={handleShowFullList} />
					)}
					{!enableFullList && totalCount !== null && (
						<PaginationDetails itemsCount={itemCount} totalCount={totalCount} />
					)}
				</Footer>
			)}
			{showFooterType === 'custom' && CustomFooter}
			<ShortcutScope />
		</>
	);
};

export default PaginatedAsyncSelectView;

export const PaginatedAsyncSelectViewExperience = ({
	props,
}: EntryPointProps<{}, {}, Props, {}>) => {
	return <PaginatedAsyncSelectView {...props} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Footer = styled.div({
	paddingTop: token('space.050'),
	paddingBottom: token('space.050'),
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	borderTop: `${token('color.border')} solid 1px`,
	height: '40px',
});
